import * as React from 'react';
import { Box, Dialog, DialogTitle, DialogContent, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGridPro } from '@mui/x-data-grid-pro';
import moment from 'moment';
import { useGetPaymentHistoryQuery } from '../../../services/InsuranceService';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

export const PaymentHistoryModal = ({ open, onClose, paymentKey }) => {
  const { data, isLoading, error, refetch } = useGetPaymentHistoryQuery(paymentKey, {
    skip: !open || !paymentKey,
  });

  React.useEffect(() => {
    if (open && paymentKey) refetch();
  }, [open, paymentKey, refetch]);

  // Match pill styling/colors used in invoices grid
  const renderActivityTypePill = (desc) => {
    if (!desc) return <Box component="span" sx={{ fontSize: 12, color: 'gray', ml: 1.5 }}>N/A</Box>;
    const l = String(desc).toLowerCase();
    let bg = '#e5e7eb';
    let fg = '#374151';
    let Icon = null;
    // Applied -> green, Void -> gray, Refund -> blue, Reversed -> orange/red
    if (l === 'applied') { bg = '#dff6dd'; fg = '#1e4620'; Icon = CheckCircleOutlineIcon; }
    else if (l === 'void') { bg = '#f3f4f6'; fg = '#6b7280'; Icon = BlockOutlinedIcon; }
    else if (l === 'refund') { bg = '#e0f2fe'; fg = '#075985'; Icon = AutorenewIcon; }
    else if (l === 'reversed') { bg = '#fed9cc'; fg = '#d83b01'; Icon = RotateLeftIcon; }
    return (
      <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', px: 1, borderRadius: '15px', fontSize: 12,
        fontWeight: 600, bgcolor: bg, color: fg, textTransform: 'capitalize', minWidth: 60,
        height: 22, lineHeight: 1, ml: 0.5
      }}>
        {Icon && <Icon sx={{ fontSize: 14, mr: 0.5 }} />}{desc}
      </Box>
    );
  };

  const columns = React.useMemo(() => [
    { field: 'activityTypeDesc', headerName: 'Type', width: 120, renderCell: (params) => renderActivityTypePill(params.row.activityTypeDesc) },
    {
      field: 'appliedAmount', headerName: 'Amount', width: 120,
      valueGetter: (value) => value ? `${Number(value).toFixed(2)}` : 'N/A'
    },
    {
      field: 'createdBy', headerName: 'Created By', width: 180,
      renderCell: (params) => {
        const name = params?.row?.loginName || 'N/A';
        const dateVal = params?.row?.activityDate;
        const formatted = dateVal ? moment.utc(dateVal).local().format('MM/DD/YYYY hh:mm A') : 'N/A';
        return (
          <Box sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 0.25,
            lineHeight: 1.2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: 16, color: 'primary.main', opacity: 0.85 }} />
              <Box component="span" sx={{ fontSize: '0.72rem' }}>{name}</Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarMonthIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.9 }} />
              <Box component="span" sx={{ fontSize: '0.70rem', color: 'text.secondary' }}>{formatted}</Box>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'naicMember', headerName: 'Applied To', flex: 1, minWidth: 220,
      renderCell: (params) => {
        const naic = params?.row?.naicCode || '';
        const member = params?.row?.memberName || '';
        const combined = naic && member ? `${naic} - ${member}` : (naic || member || '');
        return (
          <Box sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            lineHeight: 1.2,
            py: 0.25,
          }}>
            {combined || 'N/A'}
          </Box>
        );
      }
    },
    { field: 'invoiceNumber', headerName: 'Invoice #', width: 75 },  
    {
      field: 'activityNote', headerName: 'Note', flex: 1, minWidth: 100,
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          lineHeight: 1.2,
        }}>
          {params.value || 'N/A'}
        </Box>
      )
    },
  ], []);

  // listView configuration
  const [view, setView] = React.useState('grid');
  const isListView = view === 'list';
  const listViewColDef = React.useMemo(() => ({
    primary: 'invoiceNumber',
    secondary: ['activityTypeDesc', 'invoiceStatus', 'appliedAmount'],
    tertiary: ['activityDate', 'loginName'],
  }), []);

  const totalApplied = React.useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    return rows.reduce((sum, r) => {
      const v = Number(r?.appliedAmount);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
  }, [data]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Payment History
        <Tooltip title="Close">
          <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ height: 520, width: '100%', mt: 1 }}>
          <DataGridPro
            rows={Array.isArray(data) ? data : []}
            loading={isLoading}
            columns={columns}
            rowHeight={132}
            pagination
            pageSizeOptions={[10, 25, 50]}
            listView={isListView}
            listViewColumn={listViewColDef}
            sx={{
              backgroundColor: 'background.paper',
              '& .MuiDataGrid-cell': { fontSize: '0.75rem' },
              '& .MuiDataGrid-columnHeaders': { fontSize: '0.72rem' },
              '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.72rem', fontWeight: 600 },
              '& .MuiDataGrid-row': { fontSize: '0.75rem' },
              '& .MuiDataGrid-footerContainer': { fontSize: '0.72rem' },
            }}
          />
        </Box>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <Box sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
            Total Applied: {totalApplied.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentHistoryModal;
